import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

function formatDate(timestamp) {
  if (!timestamp) return '';
  const ts = timestamp.endsWith('Z') ? timestamp : timestamp + 'Z';
  const date = new Date(ts);
  const today = new Date();
  const yesterday = new Date(today);
  yesterday.setDate(yesterday.getDate() - 1);
  if (date.toDateString() === today.toDateString()) return 'today';
  if (date.toDateString() === yesterday.toDateString()) return 'yesterday';
  return date.toLocaleDateString([], { month: 'long', day: 'numeric', year: 'numeric' });
}

function renderText(text) {
  if (!text) return null;
  return text.split('\n').map((line, i, arr) => {
    const parts = line.split(/(\*[^*]+\*)/g);
    return (
      <span key={i}>
        {parts.map((part, j) => {
          if (part.startsWith('*') && part.endsWith('*') && part.length > 2) {
            return <em key={j}>{part.slice(1, -1)}</em>;
          }
          return <span key={j}>{part}</span>;
        })}
        {i < arr.length - 1 && <br />}
      </span>
    );
  });
}

function Library() {
  const [tab, setTab] = useState('favorites');
  const [favorites, setFavorites] = useState([]);
  const [audioMessages, setAudioMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [playingId, setPlayingId] = useState(null);
  const [audioProgress, setAudioProgress] = useState({});
  const [audioDurations, setAudioDurations] = useState({});
  const currentAudioRef = useRef(null);
  const { companionId } = useParams();
  const navigate = useNavigate();
  const token = localStorage.getItem('token');

  useEffect(() => {
    fetchAll();
  }, []);

  const fetchAll = async () => {
    setLoading(true);
    try {
      const [favRes, audioRes] = await Promise.all([
        axios.get(`${API}/library/${companionId}/favorites?token=${token}`),
        axios.get(`${API}/library/${companionId}/audio?token=${token}`),
      ]);
      setFavorites(favRes.data.messages || []);
      setAudioMessages(audioRes.data.messages || []);
    } catch(err) {
      console.log(err);
    }
    setLoading(false);
  };

  const stopAudio = () => {
    if (currentAudioRef.current) {
      currentAudioRef.current.pause();
      currentAudioRef.current = null;
    }
    setPlayingId(null);
  };

  const playAudio = async (message) => {
    if (playingId === message.message_id) { stopAudio(); return; }
    stopAudio();
    if (!message.audio_url) return;
    try {
      setPlayingId(message.message_id);
      const audio = new Audio(message.audio_url);
      currentAudioRef.current = audio;
      audio.addEventListener('loadedmetadata', () => {
        setAudioDurations(prev => ({ ...prev, [message.message_id]: audio.duration }));
      });
      audio.addEventListener('timeupdate', () => {
        setAudioProgress(prev => ({ ...prev, [message.message_id]: audio.currentTime }));
      });
      audio.addEventListener('ended', () => {
        setPlayingId(null);
        setAudioProgress(prev => ({ ...prev, [message.message_id]: 0 }));
        currentAudioRef.current = null;
      });
      await audio.play();
    } catch(err) {
      console.log('audio error:', err);
      setPlayingId(null);
    }
  };

  const scrub = (messageId, e) => {
    if (!currentAudioRef.current || playingId !== messageId) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const x = (e.touches ? e.touches[0].clientX : e.clientX) - rect.left;
    const pct = Math.max(0, Math.min(1, x / rect.width));
    currentAudioRef.current.currentTime = pct * (audioDurations[messageId] || 0);
  };

  const formatTime = (secs) => {
    if (!secs || isNaN(secs)) return '0:00';
    const m = Math.floor(secs / 60);
    const s = Math.floor(secs % 60);
    return `${m}:${String(s).padStart(2, '0')}`;
  };

  const unfavorite = async (message) => {
    try {
      await axios.put(`${API}/messages/${message.message_id}/favorite?token=${token}`, {
        token,
        is_favorite: false,
        favorite_note: null,
      });
      setFavorites(prev => prev.filter(m => m.message_id !== message.message_id));
    } catch(err) {
      console.log('unfavorite error:', err);
    }
  };

  const AudioPlayer = ({ message }) => {
    const id = message.message_id;
    const progress = audioProgress[id] || 0;
    const duration = audioDurations[id] || 0;
    const pct = duration > 0 ? (progress / duration) * 100 : 0;
    const isPlaying = playingId === id;
    return (
      <div style={styles.audioPlayer}>
        <button style={styles.playBtn} onClick={() => playAudio(message)}>
          {isPlaying ? '⏸' : '▶'}
        </button>
        <div style={styles.playerMiddle}>
          <div
            style={styles.scrubberWrap}
            onClick={(e) => scrub(id, e)}
            onTouchEnd={(e) => scrub(id, e)}
          >
            <div style={{...styles.scrubberFill, width: `${pct}%`}} />
            <div style={{...styles.scrubberThumb, left: `${pct}%`}} />
          </div>
          <div style={styles.timeRow}>
            <span style={styles.timeCurrent}>{formatTime(progress)}</span>
            <span style={styles.timeTotal}>{formatTime(duration)}</span>
          </div>
        </div>
      </div>
    );
  };

  const FavoriteCard = ({ message }) => (
    <div style={styles.card}>
      <div style={styles.cardTop}>
        <span style={styles.starLabel}>★</span>
        <span style={styles.dateLabel}>{formatDate(message.timestamp)}</span>
        <button style={styles.removeBtn} onClick={() => unfavorite(message)}>✕</button>
      </div>
      <div style={styles.cardContent}>{renderText(message.content)}</div>
      {message.favorite_note ? (
        <div style={styles.noteWrap}>
          <div style={styles.noteAccent} />
          <div style={styles.noteText}>{message.favorite_note}</div>
        </div>
      ) : null}
      {message.audio_url && <AudioPlayer message={message} />}
    </div>
  );

  const AudioCard = ({ message }) => (
    <div style={styles.card}>
      <div style={styles.cardTop}>
        <span style={styles.dateLabel}>{formatDate(message.timestamp)}</span>
      </div>
      <div style={styles.cardContent}>{renderText(message.content)}</div>
      <AudioPlayer message={message} />
    </div>
  );

  const currentMessages = tab === 'favorites' ? favorites : audioMessages;

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <button style={styles.backBtn} onClick={() => navigate(-1)}>←</button>
        <div style={styles.headerTitleWrap}>
          <div style={styles.headerTitle}>library</div>
          <div style={styles.headerSub}>your saved moments ♡</div>
        </div>
        <div style={{width: 36}} />
      </div>

      {/* TABS */}
      <div style={styles.tabs}>
        <button
          style={{...styles.tab, ...(tab === 'favorites' ? styles.tabActive : {})}}
          onClick={() => setTab('favorites')}
        >
          ★ favorites
        </button>
        <button
          style={{...styles.tab, ...(tab === 'audio' ? styles.tabActive : {})}}
          onClick={() => setTab('audio')}
        >
          🎙️ audio
        </button>
      </div>

      {/* CONTENT */}
      <div style={styles.content}>
        {loading && <p style={styles.hint}>loading… ♡</p>}

        {!loading && currentMessages.length === 0 && (
          <div style={styles.emptyState}>
            <div style={styles.emptyIcon}>{tab === 'favorites' ? '★' : '🎙️'}</div>
            <div style={styles.emptyTitle}>
              {tab === 'favorites' ? 'nothing saved yet' : 'no audio yet'}
            </div>
            <div style={styles.emptyHint}>
              {tab === 'favorites'
                ? 'long press any message to save it ♡'
                : 'generate voice on a message to see it here ♡'}
            </div>
          </div>
        )}

        {!loading && currentMessages.map((m, i) => (
          tab === 'favorites'
            ? <FavoriteCard key={m.message_id || i} message={m} />
            : <AudioCard key={m.message_id || i} message={m} />
        ))}
      </div>
    </div>
  );
}

const styles = {
  container: {
    position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
    background: '#0e0b0f', fontFamily: "'DM Sans', sans-serif",
    display: 'flex', flexDirection: 'column',
  },
  header: {
    padding: '24px 28px 20px',
    background: 'linear-gradient(180deg, #120f18 0%, #0e0b0f 100%)',
    borderBottom: '1px solid rgba(201,147,90,0.08)',
    display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexShrink: 0,
  },
  backBtn: {
    width: '36px', height: '36px', borderRadius: '50%',
    border: '1px solid rgba(201,147,90,0.15)', background: 'transparent',
    color: '#c9935a', fontSize: '16px', cursor: 'pointer',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
  },
  headerTitleWrap: { display: 'flex', flexDirection: 'column', alignItems: 'center' },
  headerTitle: {
    fontFamily: "'Cormorant Garamond', serif",
    fontSize: '22px', color: '#e8ddd0', fontWeight: '300', fontStyle: 'italic',
  },
  headerSub: { fontSize: '11px', color: '#4a3d30', fontWeight: '300', marginTop: '2px' },
  tabs: {
    display: 'flex', borderBottom: '1px solid rgba(201,147,90,0.08)',
    flexShrink: 0,
  },
  tab: {
    flex: 1, padding: '14px', border: 'none', background: 'transparent',
    fontFamily: "'DM Sans', sans-serif", fontSize: '13px',
    color: '#4a3d30', fontWeight: '300', cursor: 'pointer',
    borderBottom: '2px solid transparent', transition: 'all 0.2s',
  },
  tabActive: {
    color: '#c9935a',
    borderBottom: '2px solid #c9935a',
  },
  content: {
    flex: 1, overflowY: 'auto', WebkitOverflowScrolling: 'touch',
    padding: '20px 20px 80px',
    display: 'flex', flexDirection: 'column', gap: '14px',
  },
  hint: { fontSize: '13px', color: '#2a1f16', textAlign: 'center', padding: '40px 0' },
  emptyState: {
    display: 'flex', flexDirection: 'column',
    alignItems: 'center', gap: '12px', paddingTop: '80px',
  },
  emptyIcon: { fontSize: '32px', color: '#2a1f16' },
  emptyTitle: {
    fontFamily: "'Cormorant Garamond', serif",
    fontSize: '22px', color: '#4a3d30', fontWeight: '300', fontStyle: 'italic',
  },
  emptyHint: { fontSize: '12px', color: '#2a1f16', fontWeight: '300', textAlign: 'center' },
  card: {
    background: '#1e1825', borderRadius: '20px',
    border: '1px solid rgba(201,147,90,0.08)',
    padding: '16px 18px',
    display: 'flex', flexDirection: 'column', gap: '10px',
  },
  cardTop: {
    display: 'flex', alignItems: 'center', gap: '8px',
  },
  starLabel: { fontSize: '12px', color: '#c9935a' },
  dateLabel: { fontSize: '11px', color: '#3a2a1a', fontWeight: '300', flex: 1 },
  removeBtn: {
    width: '24px', height: '24px', borderRadius: '50%',
    border: 'none', background: 'transparent',
    color: '#3a2a1a', fontSize: '11px', cursor: 'pointer',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
  },
  cardContent: {
    fontSize: '15px', color: '#e8ddd0', fontWeight: '300', lineHeight: '1.7',
  },
  noteWrap: {
    display: 'flex', gap: '8px', alignItems: 'flex-start',
  },
  noteAccent: {
    width: '2px', borderRadius: '2px', flexShrink: 0,
    background: 'linear-gradient(180deg, #8a5f35, transparent)',
    alignSelf: 'stretch', minHeight: '16px',
  },
  noteText: {
    fontSize: '12px', color: '#8a5f35', fontWeight: '300',
    lineHeight: '1.5', fontStyle: 'italic',
  },
  audioPlayer: {
    display: 'flex', alignItems: 'center', gap: '10px',
    background: '#0e0b0f', borderRadius: '12px', padding: '10px 12px',
  },
  playBtn: {
    width: '30px', height: '30px', borderRadius: '50%', border: 'none',
    background: 'linear-gradient(135deg, #8a5f35, #c9935a)',
    color: '#0e0b0f', fontSize: '12px', cursor: 'pointer',
    display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
  },
  playerMiddle: { flex: 1, display: 'flex', flexDirection: 'column', gap: '5px' },
  scrubberWrap: {
    position: 'relative', height: '4px', background: 'rgba(201,147,90,0.15)',
    borderRadius: '2px', cursor: 'pointer',
  },
  scrubberFill: {
    height: '100%', background: 'linear-gradient(90deg, #8a5f35, #c9935a)',
    borderRadius: '2px', transition: 'width 0.1s',
  },
  scrubberThumb: {
    position: 'absolute', width: '10px', height: '10px', borderRadius: '50%',
    background: '#c9935a', top: '50%', transform: 'translate(-50%, -50%)',
    boxShadow: '0 0 4px rgba(201,147,90,0.5)', transition: 'left 0.1s',
  },
  timeRow: { display: 'flex', justifyContent: 'space-between' },
  timeCurrent: { fontSize: '10px', color: '#c9935a', fontWeight: '300', fontFamily: "'DM Sans', sans-serif" },
  timeTotal: { fontSize: '10px', color: '#4a3d30', fontWeight: '300', fontFamily: "'DM Sans', sans-serif" },
};

export default Library;
